import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

function fmtUptime(seconds) {
  if (!seconds || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d >= 1) return `${d}d ${h}h`;
  if (h >= 1) return `${h}h ${m}m`;
  if (m >= 1) return `${m}m`;
  return `${seconds}s`;
}

const SOURCE_COLORS = {
  ok: "bg-emerald-400",
  down: "bg-red-500",
  unknown: "bg-[var(--tt-text-faint)]",
};

export default function ApiStatusBadge() {
  const [status, setStatus] = useState("checking"); // "checking" | "up" | "down"
  const [uptime, setUptime] = useState(0);
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    const check = async () => {
      try {
        const { data } = await api.get("/health", { timeout: 4000 });
        if (cancel) return;
        setStatus(data?.ok ? "up" : "down");
        setUptime(data?.uptime_seconds || 0);
        setSources(data?.sources || []);
      } catch {
        if (!cancel) setStatus("down");
      }
    };
    check();
    const id = setInterval(check, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, []);

  const dotClass = {
    checking: "bg-[var(--tt-text-muted)]",
    up: "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)] animate-pulse",
    down: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.7)]",
  }[status];

  const label = status === "up" ? "API online" : status === "down" ? "API offline" : "Checking…";

  const stats = useMemo(() => {
    const counts = { ok: 0, down: 0, unknown: 0 };
    sources.forEach((s) => { counts[s.state] = (counts[s.state] || 0) + 1; });
    return counts;
  }, [sources]);

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="api-status-badge"
        onClick={() => setOpen((o) => !o)}
        title={status === "up" ? `Uptime: ${fmtUptime(uptime)} · click for source health` : label}
        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--tt-surface)] border border-[var(--tt-border)] text-[10px] font-mono-tt text-[var(--tt-text-muted)] select-none hover:bg-[var(--tt-surface-2)] transition-colors"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
        <span className="truncate flex-1 text-left">{label}</span>
        {status === "up" && sources.length > 0 && (
          <span className="flex items-center gap-0.5" aria-label="source health">
            <span className="text-[var(--tt-text-faint)]">{stats.ok}</span>
            <span className="w-1 h-1 rounded-full bg-emerald-400" />
            {stats.down > 0 && (
              <>
                <span className="text-red-400 ml-1">{stats.down}</span>
                <span className="w-1 h-1 rounded-full bg-red-500" />
              </>
            )}
          </span>
        )}
      </button>

      {open && sources.length > 0 && (
        <div
          data-testid="api-status-popover"
          className="absolute bottom-full left-0 right-0 mb-2 p-3 rounded-xl bg-[var(--tt-surface-2)] border border-[var(--tt-border)] shadow-xl shadow-black/40 z-50"
        >
          <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-2">
            Torrent Sources
          </div>
          <ul className="space-y-1.5">
            {sources.map((s) => (
              <li key={s.name} className="flex items-center gap-2 text-[11px]">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SOURCE_COLORS[s.state]}`} />
                <span className="flex-1 truncate font-medium text-[var(--tt-text)]">{s.name}</span>
                <div className="flex flex-col items-end text-[10px] font-mono-tt leading-tight">
                  <span className="text-[var(--tt-text-faint)]">
                    {s.state === "ok" ? `${s.results || 0} hits` : s.state === "down" ? "down" : "—"}
                  </span>
                  {s.uptime_24h_pct != null && (
                    <span
                      className={
                        s.uptime_24h_pct >= 95
                          ? "text-emerald-400"
                          : s.uptime_24h_pct >= 75
                            ? "text-[var(--tt-yellow)]"
                            : "text-red-400"
                      }
                      title={`${s.probes_24h} probes in 24h`}
                    >
                      {s.uptime_24h_pct}% / 24h
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {uptime > 0 && (
            <div className="mt-2 pt-2 border-t border-[var(--tt-border)] text-[10px] text-[var(--tt-text-faint)]">
              Uptime: {fmtUptime(uptime)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
